import React, { useEffect, useState } from 'react';
import { getAllAgents, createAgent, updateAgent, deleteAgent } from '../../api/agentApi';
import './ManageAgents.css';

export default function ManageAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    mobile: '',
    expertise: '',
    max_concurrent_chats: 3
  });

  const fetchAgentsData = async () => {
    try {
      setLoading(true);
      const result = await getAllAgents();
      setAgents(result.agents || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgentsData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'max_concurrent_chats' ? parseInt(value) : value
    }));
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    try {
      await createAgent(formData);
      alert('✅ Agent created successfully!');
      setShowCreateForm(false);
      resetForm();
      fetchAgentsData();
    } catch (error) {
      alert('❌ Failed to create agent: ' + (error.response?.data?.msg || error.message));
    }
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    try {
      const { password, ...updateData } = formData; // Don't send password in update
      await updateAgent(editingAgent.id, updateData);
      alert('✅ Agent updated successfully!');
      setEditingAgent(null);
      resetForm();
      fetchAgentsData();
    } catch (error) {
      alert('❌ Failed to update agent: ' + (error.response?.data?.msg || error.message));
    }
  };

  const handleDeactivateAgent = async (agentId, agentName) => {
    if (window.confirm(`Are you sure you want to deactivate ${agentName}?`)) {
      try {
        await deleteAgent(agentId);
        alert('✅ Agent deactivated successfully!');
        fetchAgentsData();
      } catch (error) {
        alert('❌ Failed to deactivate agent: ' + (error.response?.data?.msg || error.message));
      }
    }
  };

  const startEditing = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      email: agent.email,
      password: '',
      mobile: agent.mobile || '',
      expertise: agent.expertise || '',
      max_concurrent_chats: agent.max_concurrent_chats || 3
    });
    setShowCreateForm(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      mobile: '',
      expertise: '',
      max_concurrent_chats: 3
    });
  };

  const cancelForm = () => {
    setShowCreateForm(false);
    setEditingAgent(null);
    resetForm();
  };

  if (loading) {
    return <div className="manage-agents__loading">Loading agents...</div>;
  }

  return (
    <div className="manage-agents">
      <div className="manage-agents__header">
        <h1>Manage Agents</h1>
        <button 
          className="manage-agents__btn-add"
          onClick={() => {
            setShowCreateForm(true);
            setEditingAgent(null);
            resetForm();
          }}
        >
          + Add New Agent
        </button>
      </div>

      {/* Create/Edit Form */}
      {(showCreateForm || editingAgent) && (
        <div className="manage-agents__form-container">
          <form 
            className="manage-agents__form"
            onSubmit={editingAgent ? handleUpdateAgent : handleCreateAgent}
          >
            <h3>{editingAgent ? 'Edit Agent' : 'Create New Agent'}</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter agent name"
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="agent@company.com"
                  disabled={!!editingAgent}
                />
              </div>
            </div>

            {!editingAgent && (
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter password"
                  minLength="6"
                />
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Mobile</label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder="Mobile number"
                />
              </div>

              <div className="form-group">
                <label>Max Concurrent Chats</label>
                <input
                  type="number"
                  name="max_concurrent_chats"
                  value={formData.max_concurrent_chats}
                  onChange={handleInputChange}
                  min="1"
                  max="20"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Expertise</label>
              <input
                type="text"
                name="expertise"
                value={formData.expertise}
                onChange={handleInputChange}
                placeholder="e.g., Technical, Billing, Sales"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingAgent ? 'Update Agent' : 'Create Agent'}
              </button>
              <button type="button" className="btn-secondary" onClick={cancelForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Agents Table */}
      <div className="manage-agents__table-container">
        <table className="manage-agents__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Expertise</th>
              <th>Status</th>
              <th>Active</th>
              <th>Max Chats</th>
              <th>Current Load</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {agents.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">No agents found</td>
              </tr>
            ) : (
              agents.map(agent => (
                <tr key={agent.id}>
                  <td>{agent.name}</td>
                  <td>{agent.email}</td>
                  <td>{agent.mobile || '-'}</td>
                  <td>{agent.expertise || '-'}</td>
                  <td>
                    <span className={`status-badge status-${agent.status.toLowerCase()}`}>
                      {agent.status}
                    </span>
                  </td>
                  <td>
                    <span className={`active-badge ${agent.is_active ? 'active' : 'inactive'}`}>
                      {agent.is_active ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>{agent.max_concurrent_chats}</td>
                  <td>{agent.current_chats || 0}</td>
                  <td className="actions">
                    <button 
                      className="btn-edit"
                      onClick={() => startEditing(agent)}
                    >
                      Edit
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeactivateAgent(agent.id, agent.name)}
                      disabled={!agent.is_active}
                    >
                      Deactivate
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
